/**
 * Support widget — the fail-closed fallback.
 *
 * Rule: a non-200, a body that is not the expected JSON (Cloudflare bot management
 * answers real visitors with an HTML challenge page) or a request that never lands all
 * resolve to *this*. No surface may invent its own version of "we could not tell".
 *
 * Both consumers used to carry their own copy, and the two disagreed on `widget.enabled`
 * and `timer_seconds`. The values below are the Django ones, because they are the ones a
 * surface actually reads: an offline widget whose contact form still works is better than
 * no widget at all. Surfaces that should vanish when support is closed — the exit-intent
 * panel, for one — branch on `state`, which is `offline` here, so they are unaffected.
 */
import type { SupportApiResponse } from "./api.types"
import { SUPPORT_DEFAULT_TIMER_SECONDS } from "./constants"

/**
 * A fresh closed payload. A factory rather than a shared constant, so no caller can
 * mutate the fallback out from under every other caller — this object is handed
 * straight into a Redux store on one surface and into the DOM adapter on another.
 */
export function closedSupportResponse(): SupportApiResponse {
  return {
    state: "offline",
    is_open: false,
    valid_until: null,
    channels: {
      chat: { enabled: false, provider: "" },
      call: { enabled: false, phone: "" },
      // The one channel that still works with no backend answer: a plain link to a form.
      contact_form: { enabled: true },
    },
    schedule: { timezone: "", weekdays: [], opens_at: "", closes_at: "" },
    widget: { enabled: true, timer_seconds: SUPPORT_DEFAULT_TIMER_SECONDS, show_outside_working_hours: true },
    flags: { use_salesforce_chat: false, use_salesforce_chat_new: false, use_salesforce_contact_us: false },
  }
}
